import { QUIZ_ROUTES } from "./quizRoutes";
import { useQuizBrowserBack } from './quizHistoryGuard';
import React, { useEffect, useMemo, useRef, useState } from "react";
import Swal from "sweetalert2";
import { useLocation, useNavigate } from "react-router-dom";
import HomeDashboardLayout from "../HomeDashboardLayout";
import QuizzCard from "./QuizzCard";
import { useSeriesQuizList } from "../../hooks/useSeriesQuizList";
import { useSeriesEnroll } from "../../hooks/useSeriesEnroll";
import "../../dashboard/Dashboard.css";
import "../HomeDashboard.css";
import "../../questionBank/QuestionBankList.css";
import "../../homeDashboard(category)/Categories.css";
import heroImg from "../../assets/images/im4.png";
import {
  buildQuizBackTarget,
  buildQuizFlowState,
  getStoredQuizFlowState,
  persistQuizFlowState,
  resolveQuizBackTarget,
  navigateQuizBack,
} from "./quizFlowState";

function buildVisiblePages(currentPage, totalPages) {
  const maxVisiblePages = 5;

  if (totalPages <= maxVisiblePages) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const halfWindow = Math.floor(maxVisiblePages / 2);
  let startPage = Math.max(1, currentPage - halfWindow);
  let endPage = startPage + maxVisiblePages - 1;

  if (endPage > totalPages) {
    endPage = totalPages;
    startPage = endPage - maxVisiblePages + 1;
  }

  return Array.from({ length: endPage - startPage + 1 }, (_, index) => startPage + index);
}

const QuizSummaryShimmer = () => (
  <div className="qbl-summaryRow" aria-hidden="true">
    <span className="dashboard-skeleton dashboard-skeleton--title qbl-summaryRow__shimmer-title" />
  </div>
);

const TestSeriesQuizList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const storedFlowState = useMemo(() => getStoredQuizFlowState(), []);
  const flowState = useMemo(
    () => buildQuizFlowState(location.state, storedFlowState),
    [location.state, storedFlowState]
  );
  const selectedStateId = Number(flowState?.selectedState?.state_id || flowState?.state_id || 13);
  const selectedCategoryId = 1;
  const selectedSeriesId = Number(
    flowState?.series_id ||
      flowState?.selectedSeries?.series_id ||
      flowState?.selectedSeries?.id ||
      location.state?.series_id ||
      0
  );
  const selectedStateName =
    flowState?.selectedState?.state_name || flowState?.stateName || "Himachal Pradesh";
  const selectedSeriesName =
    flowState?.seriesName ||
    flowState?.selectedSeries?.title ||
    flowState?.selectedSeries?.name ||
    "Test Series";
  const selectedQuizOption = flowState?.selectedQuizOption || "Test Series";
  const [currentPage, setCurrentPage] = useState(Number(flowState.seriesListQueryState?.currentPage || 1));
  const listScope = JSON.stringify([selectedCategoryId, selectedSeriesId, selectedStateId]);
  const previousScope = useRef(listScope);
  const seriesEnrollMutation = useSeriesEnroll();

  useEffect(() => {
    if (previousScope.current === listScope) return;
    previousScope.current = listScope;
    setCurrentPage(1);
  }, [listScope]);

  const queryParams = useMemo(
    () => ({
      states_id: selectedStateId,
      category_id: selectedCategoryId,
      series_id: selectedSeriesId,
      page: currentPage,
    }),
    [currentPage, selectedCategoryId, selectedSeriesId, selectedStateId]
  );

  const { data, isLoading, isFetching } = useSeriesQuizList(queryParams, {
    enabled:
      Number.isFinite(selectedStateId) &&
      selectedStateId > 0 &&
      Number.isFinite(selectedCategoryId) &&
      selectedCategoryId > 0 &&
      Number.isFinite(selectedSeriesId) &&
      selectedSeriesId > 0,
  });

  const isPending = isLoading || isFetching;
  const resolvedCurrentPage = currentPage;
  const resolvedTotalPages = Number(data?.totalPages || 1);
  const visiblePages = useMemo(
    () => buildVisiblePages(resolvedCurrentPage, resolvedTotalPages),
    [resolvedCurrentPage, resolvedTotalPages]
  );
  const resolvedStateName = data?.stateName || selectedStateName;
  const resolvedSeriesName = data?.seriesInfo?.name || data?.seriesName || selectedSeriesName;
  const seriesInfo = data?.seriesInfo || {};
  const categoriesBackTarget = useMemo(
    () => ({
      pathname: QUIZ_ROUTES.list,
      state: flowState?.quizBackTarget?.state || {
        selectedState: {
          state_id: selectedStateId,
          state_name: resolvedStateName,
        },
        state_id: selectedStateId,
        stateName: resolvedStateName,
        category_id: selectedCategoryId,
        route_key: 2,
        selectedQuizOption,
      },
    }),
    [flowState?.quizBackTarget?.state, resolvedStateName, selectedCategoryId, selectedQuizOption, selectedStateId]
  );

  const seriesNavigationState = useMemo(
    () => ({
      ...flowState,
      selectedQuiz: null,
      quizData: {},
      selectedAttempt: null,
      selectedMode: null,
      assesment_batch_id: 0,
      resume_on_open: false,
      selectedState: {
        state_id: selectedStateId,
        state_name: resolvedStateName,
      },
      state_id: selectedStateId,
      stateName: resolvedStateName,
      category_id: selectedCategoryId,
      route_key: 2,
      selectedQuizOption,
      listBannerImage: data?.bannerImage || flowState?.listBannerImage || heroImg,
      series_id: selectedSeriesId,
      seriesName: resolvedSeriesName,
      selectedSeries: {
        id: selectedSeriesId,
        series_id: selectedSeriesId,
        title: resolvedSeriesName,
        name: resolvedSeriesName,
      },
      quizListPath: QUIZ_ROUTES.testSeriesList,
      seriesListQueryState: { currentPage },
      quizBackTarget: resolveQuizBackTarget(flowState, QUIZ_ROUTES.testSeriesList, buildQuizBackTarget(QUIZ_ROUTES.list, categoriesBackTarget.state)),
    }),
    [
      categoriesBackTarget.state,
      data?.bannerImage,
      flowState,
      currentPage,
      resolvedSeriesName,
      resolvedStateName,
      selectedCategoryId,
      selectedQuizOption,
      selectedSeriesId,
      selectedStateId,
    ]
  );

  useEffect(() => {
    persistQuizFlowState(buildQuizFlowState(seriesNavigationState));
  }, [seriesNavigationState]);
  const listBackTarget = resolveQuizBackTarget(flowState, QUIZ_ROUTES.testSeriesList, categoriesBackTarget);
  useQuizBrowserBack(listBackTarget);

  const handlePageChange = (page) => {
    if (page === currentPage || isPending) {
      return;
    }

    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSeriesEnroll = async () => {
    if (
      seriesEnrollMutation.isPending ||
      !selectedStateId ||
      !selectedCategoryId ||
      !selectedSeriesId ||
      seriesInfo.enrolledStatus
    ) {
      return;
    }

    try {
      const result = await seriesEnrollMutation.mutateAsync({
        states_id: selectedStateId,
        category_id: selectedCategoryId,
        series_id: selectedSeriesId,
      });

      await Swal.fire({
        icon: "success",
        title: "Enrollment successful",
        text:
          result?.meta?.message ||
          "You have been enrolled in this test series.",
        confirmButtonColor: "#5b34ea",
      });
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "Unable to enroll",
        text: error?.message || "Please try again.",
        confirmButtonColor: "#5b34ea",
      });
    }
  };

  const handleBeforeQuizOpen = async () => {
    if (Number(seriesInfo.enrolledStatus) === 1) return true;
    const confirmation = await Swal.fire({
      title: 'Enroll in this test series',
      text: 'Enroll before opening a quiz in this series.',
      icon: 'info', showCancelButton: true, confirmButtonText: 'Enroll',
      confirmButtonColor: '#5b34ea',
    });
    if (!confirmation.isConfirmed) return false;
    try {
      await seriesEnrollMutation.mutateAsync({
        states_id: selectedStateId, category_id: selectedCategoryId, series_id: selectedSeriesId,
      });
      return true;
    } catch (error) {
      await Swal.fire({ icon: 'error', title: 'Unable to enroll', text: error?.message || 'Please try again.' });
      return false;
    }
  };

  const seriesMetaItems = [
    {
      icon: "bi bi-journal-text",
      value: seriesInfo.totalQuiz || 0,
      label: "Total Quizzes",
      accent: "#5b34ea",
      tone: "rgba(91, 52, 234, 0.10)",
    },
    {
      icon: "bi bi-calendar3",
      value: seriesInfo.startDate || "TBA",
      label: "Start Date",
      accent: "#1ea672",
      tone: "rgba(30, 166, 114, 0.10)",
    },
    {
      icon: "bi bi-calendar-check",
      value: seriesInfo.endDate || "-",
      label: "End Date",
      accent: "#ef8a17",
      tone: "rgba(239, 138, 23, 0.10)",
    },
    {
      icon: "bi bi-people",
      value: seriesInfo.totalEnrollments || 0,
      label: "Enrollments",
      accent: "#1677ff",
      tone: "rgba(22, 119, 255, 0.10)",
    },
  ];

  const statusLabel = seriesInfo.enrolledStatus ? "Enrolled" : "Available";

  return (
    <>
      <style>{`
        .homeDashboard-wrapper .page-content {
          padding-right: 0;
        }
        .homeDashboard-wrapper .main-content {
          background: #ebe9f7;
        }
        .series-info-card {
          margin-top: 18px;
          //border: 1px solid #e4dcff;
          //border-radius: 24px;
          //background: linear-gradient(140deg, #ffffff 0%, #faf7ff 52%, #eff7ff 100%);
          //box-shadow: 0 18px 40px rgba(58, 42, 126, 0.08);
          overflow: hidden;
        }
        .series-info-card__inner {
          display: grid;
          grid-template-columns: minmax(0, 1.2fr) minmax(320px, 0.9fr);
          gap: 24px;
          padding: 26px 0;
        }
        .series-info-card__lead {
          display: flex;
          flex-direction: column;
          gap: 18px;
        }
        .series-info-card__header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 16px;
        }
        .series-info-card__statusWrap {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
          justify-content: flex-end;
        }
        .series-info-card__status {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 14px;
          border-radius: 999px;
          background: rgba(91, 52, 234, 0.10);
          color: #5b34ea;
          font-size: 13px;
          font-weight: 700;
          white-space: nowrap;
        }
        .series-info-card__enroll {
          border: none;
          border-radius: 999px;
          background: linear-gradient(135deg, #68bc00 0%, #5ea900 100%);
          color: #fff;
          padding: 10px 18px;
          font-size: 13px;
          font-weight: 700;
          box-shadow: 0 12px 24px rgba(104, 188, 0, 0.22);
        }
        .series-info-card__enroll:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          box-shadow: none;
        }
        .series-info-card__micro {
          color: #8a84a3;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }
        .series-info-card__title {
          margin: 6px 0 0;
          color: #1f1837;
          font-size: 24px;
          line-height: 1.3;
          font-weight: 800;
        }
        .series-info-card__description {
          margin: 0;
          color: #534f68;
          font-size: 15px;
          line-height: 1.75;
        }
        .series-info-card__foot {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }
        .series-info-card__pill {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 14px;
          border-radius: 999px;
          background: #fff;
          border: 1px solid #e8e3fb;
          color: #2b2740;
          font-size: 13px;
          font-weight: 600;
        }
        .series-info-card__stats {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 14px;
          display:none;
        }
        .series-info-card__stat {
          min-height: 122px;
          padding: 16px;
          border-radius: 20px;
          background: rgba(255, 255, 255, 0.92);
          border: 1px solid #ece7ff;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }
        .series-info-card__stat-icon {
          width: 42px;
          height: 42px;
          border-radius: 14px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
        }
        .series-info-card__stat-value {
          margin-top: 18px;
          color: #1d1734;
          font-size: 19px;
          font-weight: 800;
          line-height: 1.35;
          word-break: break-word;
        }
        .series-info-card__stat-label {
          margin-top: 6px;
          color: #7a7591;
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }
        @media (max-width: 991px) {
          .series-info-card__inner {
            grid-template-columns: 1fr;
          }
        }
        @media (max-width: 576px) {
          .series-info-card {
            border-radius: 18px;
          }
          .series-info-card__inner {
            padding: 18px;
          }
          .series-info-card__header {
            flex-direction: column;
            align-items: flex-start;
          }
          .series-info-card__statusWrap {
            // width: 100%;
            justify-content: flex-start;
          }
          .series-info-card__title {
            font-size: 21px;
          }
          .series-info-card__stats {
            grid-template-columns: 1fr;
          }
          .series-info-card__stat {
            min-height: auto;
          }
        .qbl-summaryRow {
          justify-content: right;
          display: block !important;
        }
        .series-info-card__foot {
           margin: 10px 0 5px;
         }
        }
      `}</style>
      <HomeDashboardLayout showHeader={false}>
        <div className="qbl-page qbankList-page">
          <div className="qbl-main" aria-label="Series quiz content">
            <section className="qbl-panel">
              <div className="hideProfile-page">
                <div className="categories-page-header mb-0">
                  <div>
                    <button
                      type="button"
                      className="categories-back-button"
                      onClick={() => {
                        navigateQuizBack(navigate, listBackTarget);
                      }}
                      aria-label="Go back"
                    >
                      <i className="bi bi-chevron-left"></i>
                    </button>
                  </div>
                  <div></div>
                  <div></div>
                </div>
              </div>

              <div className="qb-body">
                <div className="d-flex align-items-center justify-content-center">
                  <div className="qb-copy">
                    <div className="qb-loc">
                      <i className="bi bi-geo-alt-fill" />
                      <span>{resolvedStateName}</span>
                    </div>

                    <div className="">
                      <div className="">
                        <div className="series-info-card__lead">
                          <div className="series-info-card__header">
                            <div>
                              <div className="series-info-card__micro">Test Series</div>
                              <h1 className="series-info-card__title">{resolvedSeriesName}</h1>
                            </div>
                            {/* <div className="series-info-card__statusWrap">
                              <div className="series-info-card__status">
                                <i className="bi bi-lightning-charge-fill"></i>
                                <span>{statusLabel}</span>
                              </div>
                              {!seriesInfo.enrolledStatus ? (
                                <button
                                  type="button"
                                  className="series-info-card__enroll"
                                  onClick={handleSeriesEnroll}
                                  disabled={seriesEnrollMutation.isPending}
                                >
                                  {seriesEnrollMutation.isPending ? "Enrolling..." : "Enroll Now"}
                                </button>
                              ) : null}
                            </div> */}
                          </div>

                          <p className="series-info-card__description">
                            {seriesInfo.description || "Choose a quiz from this test series"}
                          </p>

                          {/* <div className="series-info-card__foot">
                            <span className="series-info-card__pill">
                              <i className="bi bi-collection-play"></i>
                              {data?.totalRecords || 0} {Number(data?.totalRecords || 0) === 1 ? "Quiz" : "Quizzes"}
                            </span>
                            <span className="series-info-card__pill">
                              <i className="bi bi-mortarboard"></i>
                              {seriesInfo.totalEnrollments || 0} Joined
                            </span>
                          </div> */}
                       <div class="status-filter">
                          <button type="button" class="status-btn active">All</button>
                          <button type="button" class="status-btn ">Previous</button>
                          <button type="button" class="status-btn ">Ongoing</button>
                          <button type="button" class="status-btn ">Upcoming</button>
                          <button type="button" class="status-btn ">Ended</button>
                          <button type="button" class="ad-coins-btn">
                             <span class="play-icon"><i class="bi bi-play-fill"></i></span>Ad Coins: 601
                          </button>
                          <button type="button" class="quizz-Filter-icon d-flex d-lg-flex d-xl-none">
                             <span class="quizz-Filter"><i class="bi bi-sliders2"></i></span>
                          </button>
                       </div>

                          
                        </div>

                        {!isPending ? (
                          <div className="series-info-card__stats">
                            {seriesMetaItems.map((item) => (
                              <div className="series-info-card__stat" key={item.label}>
                                <div
                                  className="series-info-card__stat-icon"
                                  style={{ background: item.tone, color: item.accent }}
                                >
                                  <i className={item.icon}></i>
                                </div>
                                <div className="series-info-card__stat-value">{item.value}</div>
                                <div className="series-info-card__stat-label">{item.label}</div>
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  <div className="qb-visual-img align-items-center">
                    <img
                      src={data?.bannerImage || flowState?.listBannerImage || heroImg}
                      alt=""
                      className="qb-hero-image"
                    />
                  </div>
                </div>
              </div>

              {isPending ? (
                <QuizSummaryShimmer />
              ) : (
                <div className="qbl-summaryRow">
                  <h2>
                    {data?.totalRecords || 0} {Number(data?.totalRecords || 0) === 1 ? "Quiz" : "Quizzes"}
                  </h2>
                   <div className="series-info-card__foot">
                            {/* <span className="series-info-card__pill">
                              <i className="bi bi-collection-play"></i>
                              {data?.totalRecords || 0} {Number(data?.totalRecords || 0) === 1 ? "Quiz" : "Quizzes"}
                            </span> */}
                              <div className="series-info-card__statusWrap">
                              <div className="series-info-card__status">
                                <i className="bi bi-lightning-charge-fill"></i>
                                <span>{statusLabel}</span>
                              </div>
                              {!seriesInfo.enrolledStatus ? (
                                <button
                                  type="button"
                                  className="series-info-card__enroll"
                                  onClick={handleSeriesEnroll}
                                  disabled={seriesEnrollMutation.isPending}
                                >
                                  {seriesEnrollMutation.isPending ? "Enrolling..." : "Enroll Now"}
                                </button>
                              ) : null}
                            </div>

                            <span className="series-info-card__pill">
                              <i className="bi bi-mortarboard"></i>
                              {seriesInfo.totalEnrollments || 0} Joined
                            </span>
                          </div>
                </div>
                
              )}

              <div className="qbl-questionList" aria-label="Series quiz list">
                <QuizzCard
                  coinBalance={data?.advertEarnedCoins || 0}
                  items={data?.quizList || []}
                  isLoading={isPending}
                  navigationState={seriesNavigationState}
                  onBeforeOpen={handleBeforeQuizOpen}
                  emptyMessage="No quizzes available for this test series."
                />
              </div>

              {!isPending && resolvedTotalPages > 1 ? (
                <div className="qbl-pagination">
                  <button
                    type="button"
                    className="qbl-pagination__nav"
                    disabled={resolvedCurrentPage <= 1}
                    onClick={() => handlePageChange(Math.max(1, resolvedCurrentPage - 1))}
                  >
                    Previous
                  </button>
                  <div className="qbl-pagination__pages">
                    {visiblePages.map((pageNumber) => (
                      <button
                        key={pageNumber}
                        type="button"
                        className={`qbl-pagination__page${
                          pageNumber === resolvedCurrentPage ? " is-active" : ""
                        }`}
                        onClick={() => handlePageChange(pageNumber)}
                      >
                        {pageNumber}
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="qbl-pagination__nav"
                    disabled={resolvedCurrentPage >= resolvedTotalPages}
                    onClick={() =>
                      handlePageChange(Math.min(resolvedTotalPages, resolvedCurrentPage + 1))
                    }
                  >
                    Next
                  </button>
                </div>
              ) : null}
            </section>
          </div>
        </div>
      </HomeDashboardLayout>
    </>
  );
};

export default TestSeriesQuizList;
